import React, { useState, useEffect } from "react";
import axios from "../utils/axiosInstance"; // Ensure this path is correct
import { Spin } from "antd";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ==========================================
// 1. Group Configuration (Keys must match Backend)
// ==========================================
const groups = [
  {
    groupLabel: "Single Digit",
    subKeys: ["singleDigits", "singleDigitsBulk", "spMotor", "oddEven"],
  },
  {
    groupLabel: "Single Pana",
    subKeys: ["singlePana", "choicePannaSpDp", "singlePanaBulk"],
  },
  {
    groupLabel: "Double Pana",
    subKeys: ["doublePana", "doublePanaBulk", "dpMotor"],
  },
  {
    groupLabel: "Jodi Digit",
    subKeys: ["groupJodi", "digitBasedJodi", "jodi", "jodiBulk", "redBracket"],
  },
  {
    groupLabel: "Triple Pana",
    subKeys: ["triplePana"],
  },
  {
    groupLabel: "Half Sangam",
    subKeys: ["halfSangamA", "halfSangamB"],
  },
  {
    groupLabel: "Full Sangam",
    subKeys: ["fullSangam"],
  },
];

const GameRate = () => {
  // State Management
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [isDataExists, setIsDataExists] = useState(false); // True = UPDATE, False = CREATE

  // ==========================================
  // 2. Fetch Data on Component Load
  // ==========================================
  useEffect(() => {
    fetchBetRates();
  }, []);

  const fetchBetRates = async () => {
    try {
      setLoading(true);
      // Backend GET Route
      const response = await axios.get(`/api/rates/getBetRates`);

      if (response.data) {
        // Data mil gaya -> Update Mode ON
        const { _id, createdAt, updatedAt, __v, ...filteredData } = response.data;
        setRates(filteredData);
        setIsDataExists(true);
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        // 404 matlab Data nahi hai -> Create Mode ON
        console.warn("⚠️ No rates found. Switching to Creation Mode.");
        setRates({});
        setIsDataExists(false);
      } else {
        console.error("❌ Error fetching rates:", error);
        toast.error("Failed to load rates from server.");
      }
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // 3. Handle Input Change (Bulk Logic)
  // ==========================================
  const handleGroupInputChange = (group, field, e) => {
    const { value } = e.target;
    // Allow typing empty string, handle number conversion later
    const rawValue = value; 

    setRates((prevRates) => {
      const updatedRates = { ...prevRates };

      // Convert to number if not empty
      const numericVal = rawValue === "" ? "" : Number(rawValue);

      // Ek group ke saare fields ko same value se update karein
      group.subKeys.forEach((key) => {
        if (field === "rate") {
          updatedRates[key] = numericVal;
        } else if (field === "value") {
          updatedRates[key + "Value"] = numericVal;
        }
      });

      return updatedRates;
    });
  };

  // ==========================================
  // 4. Save Data (Create or Update)
  // ==========================================
  const handleSave = async () => {
    try {
      setUpdating(true);
      const transformedRates = {};

      // Data Cleaning: Convert Empty/Null/NaN to 0
      groups.forEach((group) => {
        group.subKeys.forEach((key) => {
          // Rate Field
          let rateVal = rates[key];
          if (rateVal === "" || rateVal === null || rateVal === undefined || isNaN(rateVal)) {
            transformedRates[key] = 0;
          } else {
            transformedRates[key] = Number(rateVal);
          }

          // Value Field
          let valKey = key + "Value";
          let valVal = rates[valKey];
          if (valVal === "" || valVal === null || valVal === undefined || isNaN(valVal)) {
            transformedRates[valKey] = 0;
          } else {
            transformedRates[valKey] = Number(valVal);
          }
        });
      });

      console.log("📤 Sending Data:", transformedRates);

      if (isDataExists) {
        // ✅ UPDATE (PUT) - Backend must have /updateBetRates
        await axios.put(`/api/rates/updateBetRates`, transformedRates);
        toast.success("Rates updated successfully! 🎉");
      } else {
        // ✅ CREATE (POST) - Backend must have /addBetRates
        await axios.post(`/api/rates/add`, transformedRates);
        toast.success("Rates created successfully! 🚀");
        setIsDataExists(true); // Next time update hoga
      }

      // Refresh Data
      await fetchBetRates();

    } catch (error) {
      console.error("❌ Save Error:", error);
      if (error.response) {
        const status = error.response.status;
        if (status === 404) {
          toast.error("Error 404: Backend Route not found.");
        } else {
          toast.error(`Server Error: ${error.response.data.message || "Failed to save"}`);
        }
      } else {
        toast.error("Network Error. Check your internet or server.");
      }
    } finally {
      setUpdating(false);
    }
  };

  // Helper function to safely display values
  const getInputValue = (key) => {
    const val = rates[key];
    return val !== undefined && val !== null ? val : "";
  };

  // ==========================================
  // 5. Render UI
  // ==========================================
  return (
    <div className="p-6 min-h-screen bg-gray-50">
      {/* Toast Container */}
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Game Rates Management</h1>
        <div className="text-sm font-medium text-gray-500">
          Mode: <span className={isDataExists ? "text-blue-600" : "text-green-600"}>
            {isDataExists ? "Update Existing" : "Create New"}
          </span>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Spin size="large" tip="Loading Rates..." />
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map((group) => {
              // Use first subKey as representative for the group UI
              const representativeKey = group.subKeys[0];
              const representativeValueKey = group.subKeys[0] + "Value";

              return (
                <div key={group.groupLabel} className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center border-b border-gray-100 pb-6 last:border-0 last:pb-0">
                  
                  {/* Rate Input Section */}
                  <div className="flex flex-col">
                    <label className="font-semibold text-gray-700 text-sm mb-2">
                      {group.groupLabel} (Rate)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        placeholder="e.g. 10"
                        value={getInputValue(representativeKey)}
                        onChange={(e) => handleGroupInputChange(group, "rate", e)}
                        className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                      />
                      <span className="absolute right-3 top-2 text-xs text-gray-400">Pts</span>
                    </div>
                  </div>

                  {/* Winning Value Input Section */}
                  <div className="flex flex-col">
                    <label className="font-semibold text-gray-700 text-sm mb-2">
                      {group.groupLabel} (Winning Amount)
                    </label>
                    <div className="flex items-center border border-gray-300 rounded-md bg-white overflow-hidden focus-within:ring-2 focus-within:ring-green-500 focus-within:border-green-500 transition-all">
                      <span className="bg-gray-100 px-3 py-2 text-gray-600 font-semibold border-r border-gray-300">₹</span>
                      <input
                        type="number"
                        placeholder="e.g. 95"
                        value={getInputValue(representativeValueKey)}
                        onChange={(e) => handleGroupInputChange(group, "value", e)}
                        className="w-full py-2 px-3 text-sm outline-none"
                      />
                    </div>
                  </div>

                </div>
              );
            })}
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-8 pt-4 border-t flex justify-end">
          <button
            onClick={handleSave}
            disabled={updating || loading}
            className={`px-8 py-2.5 rounded-md text-white font-semibold text-sm shadow-md transition-all flex items-center gap-2
              ${updating 
                ? "bg-gray-400 cursor-not-allowed" 
                : isDataExists 
                  ? "bg-blue-600 hover:bg-blue-700 active:bg-blue-800" 
                  : "bg-green-600 hover:bg-green-700 active:bg-green-800"
              }`}
          >
            {updating ? (
              <>
                <Spin size="small" className="text-white" />
                Processing...
              </>
            ) : (
              isDataExists ? "Update Rates" : "Create Rates"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GameRate;